/**
 * Shared mutation-result feedback for the company-portal feature modules
 * (users, partners, projects, forms, tasks, roles).
 *
 * WHY THIS FILE LIVES HERE
 * -----------------------
 * It sits next to `api-response.ts` because every feature module imports it:
 * a single shared copy instead of six near-identical module-local ones — the
 * exact anti-pattern TICKET-001 exists to end. (It was created under
 * `src/modules/projects/` only because `src/utils/` was outside TICKET-004's
 * allowed_files; TICKET-009 moved it here with no change to its logic.)
 *
 * WHAT IT ADDS ON TOP OF `src/utils/api-response.ts`
 * -------------------------------------------------
 * `classifyMutationResult` answers "did this mutation succeed?". This module
 * answers the second question every call site has: "has the user ALREADY been
 * told?". `baseQueryWithReAuth` (src/services/api-slice.ts) globally calls
 * displayNotification for HTTP 400 / 500 transport errors and for any body
 * carrying errorCode '1030'. Firing a second notification for those replaces
 * the server's specific text with generic copy (the notification slice holds
 * one notification at a time), so `notifyMutationFailure` stays quiet in that
 * band and speaks for the genuinely silent one: a 2xx body with a non-'0',
 * non-'1030' errorCode (e.g. 1027, `data: ''`, `data: null`) and a transport
 * error whose status is neither 400 nor 500 (FETCH_ERROR, 403, 404, timeout).
 *
 * The STOP half is unconditional and belongs to the CALL SITE, not here: on
 * anything that is not SUCCESS — 1030 included — do not show a success toast,
 * do not navigate away, do not close the drawer/modal, and leave the user's
 * input intact.
 */
import {
  MutationResult,
  classifyMutationResult,
  extractResponsePayload,
} from './api-response';
import { displayNotification } from '../services';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * True only for a body-level success (`errorCode === '0'`). A body failure and
 * the `{ error }` transport shape are both false — it fails closed, so a new
 * backend error code can never read as success.
 */
export const isMutationSuccess = (response: unknown): boolean =>
  classifyMutationResult(response) === MutationResult.SUCCESS;

/**
 * True when `baseQueryWithReAuth` has ALREADY shown the user a notification
 * for this failure, so the call site must not show a second one.
 */
export const isReportedByGlobalLayer = (response: unknown): boolean => {
  if (!isRecord(response)) return false;

  if (isRecord(response.error)) {
    const { status } = response.error as { status?: unknown };
    return status === 400 || status === 500;
  }

  if (isRecord(response.data)) {
    return (response.data as { errorCode?: string }).errorCode === '1030';
  }

  return false;
};

/**
 * Report a failed mutation to the user, unless the global layer already did.
 *
 * `fallback.description` is used when the envelope carries no message. Only
 * `message` is ever rendered — never `details`, which sprint 3's SOC review
 * flagged as an uncontrolled server string.
 */
export const notifyMutationFailure = (
  response: unknown,
  fallback: { title: string; description: string }
): void => {
  if (isReportedByGlobalLayer(response)) return;

  const payload = extractResponsePayload(response);

  displayNotification({
    title: fallback.title,
    description: payload.message || fallback.description,
    status: 'error',
  });
};
